import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    type SxProps,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiAlertOutline } from '@mdi/js';
import { vars } from '../../../Theme';
import TimeDisplay from './TimeDisplay';
import { logoutUser } from '../../../Redux/UserSettingsSlice';
import { useTranslation } from 'react-i18next';
import { getSessionState, updateSessionState } from '../../../Redux/SessionSlice';
import { type AppDispatch, type IGlobalStore } from '../../../Redux/initStore';

/** Sx props for the idle dialog content text. */
const idleDialogContentTextSx: SxProps = {
    color: vars.white,
    backgroundColor: '#C99E22',
    borderRadius: '4px',
    display: 'math',
    marginLeft: '4px',
    padding: '0px 4px',
};

/** Sx props for the idle dialog title. */
const idleDialogTitleSx: SxProps = {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    color: '#C99E22',
};

/** Sx props for the idle dialog action buttons. */
const idleDialogActionBtnsSx: SxProps = { fontWeight: 'bold', textTransform: 'none' };

export default () => {
    const dispatch: AppDispatch = useDispatch();
    const { t } = useTranslation('translations', { keyPrefix: 'app' });

    const { data, isExpired, lastRequest, loading: requestPending } = useSelector((_store: IGlobalStore) => _store.sessionState);
    const logoutPending = useSelector((_store: IGlobalStore) => _store.userSettings.loading);

    const [showDialog, setShowDialog] = useState(false);
    const [remainingTime, setRemainingTime] = useState<number>(0);

    useEffect(() => {
        dispatch(getSessionState());
    }, []);

    useEffect(() => {
        document.addEventListener('visibilitychange', onTabFocus);
        const fetchInterval = setInterval(() => {
            dispatch(getSessionState());
        }, 60000);
        const checkInterval = setInterval(() => {
            checkSession();
        }, 1000);

        return () => {
            document.removeEventListener('visibilitychange', onTabFocus);
            clearInterval(fetchInterval);
            clearInterval(checkInterval);
        };
    }, [data, isExpired, logoutPending]);

    const onTabFocus = () => {
        if (document.visibilityState === 'visible' && !requestPending && Date.now() - lastRequest > 30000) {
            dispatch(getSessionState());
        }
    };

    const checkSession = () => {
        if (requestPending || logoutPending || data?.expirationTime === undefined) {
            return;
        }

        if (isExpired) {
            logout();
            return;
        }

        const remaining = data.expirationTime - Date.now();
        setRemainingTime(remaining);

        if (remaining > 300_000) {
            setShowDialog(false); // > 5 min
        } else if (remaining > 0) {
            setShowDialog(true); // < 5 min
        } else {
            logout(); // expired
        }
    };

    const logout = useCallback(() => {
        setShowDialog(false);

        if (!logoutPending) {
            dispatch(logoutUser());
        }
    }, [logoutPending]);

    const renewSession = () => {
        dispatch(updateSessionState());
        setShowDialog(false);
    };

    return (
        <Dialog
            open={showDialog}
            PaperProps={{
                style: {
                    border: '1px solid #C99E22',
                    borderRadius: '8px',
                },
            }}
            sx={{ backdropFilter: 'blur(10px)' }}
            onClose={renewSession}
        >
            <DialogTitle sx={idleDialogTitleSx}>
                <Icon path={mdiAlertOutline} size={1.5} style={{ marginRight: '10px' }} />
                {t('sessionTimeOutHeader')}
            </DialogTitle>

            <DialogContent>
                <DialogContentText
                    sx={{
                        fontWeight: 'bold',
                        display: 'inline',
                    }}
                >
                    {t('sessionTimeOutText')}
                </DialogContentText>

                <DialogContentText sx={idleDialogContentTextSx}>
                    <TimeDisplay ms={remainingTime} />
                    {' ' + t('minutes')}
                </DialogContentText>
            </DialogContent>

            <DialogActions>
                <Button
                    sx={idleDialogActionBtnsSx}
                    onClick={logout}
                    variant="outlined"
                    color="error"
                >
                    {t('logout')}
                </Button>
                <Button
                    sx={{ ...idleDialogActionBtnsSx, color: 'white' }}
                    onClick={renewSession}
                    variant="contained"
                >
                    {t('continue')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
